<template>
  <div class="list">
    <template v-if="sortable">
      <vue-nestable
        @input="payload => $emit('sort', payload)"
        v-bind="sortOptions"
        class="list__items"
        :style="itemsStyle"
      >
        <!-- eslint-disable vue/no-deprecated-slot-scope-attribute !-->
        <vue-nestable-handle
          slot-scope="{ index, item }"
          class="list__item list__item_draggable"
          :index="index"
          :item="item"
        >
          <span
            class="list__item-content list__item-content_type_box"
            :style="itemContentStyle"
          >
            <slot name="itemContent" :item="item" :index="index">
              {{ item[itemFieldName] }}
            </slot>
          </span>
        </vue-nestable-handle>
      </vue-nestable>
    </template>
    <!-- RB1 -->
    <ul v-else-if="vueVersion === 3" class="list__items" :style="itemsStyle">
      <li
        v-for="(item, index) in listItems"
        :key="item[itemFieldId]"
        :ref="setItemRef"
        :title="item[itemFieldName]"
        class="list__item"
      >
        <label
          @click="_handleItemClick(item, index)"
          :class="[
            'list__item-content',
            { 'list__item-content_no-toggle': !hasToggle },
          ]"
        >
          <Checkbox
            v-if="hasToggle"
            @input="selected => _handleToggleInput(index, selected)"
            :disabled="item.disabled"
            :value="item.selected"
          />
          <slot name="itemContent" :column="item" :index="index">
            {{ item[itemFieldName] }}
          </slot>
        </label>
      </li>
    </ul>
    <!-- RB1 -->
    <ul v-else class="list__items" :style="itemsStyle">
      <li
        v-for="(item, index) in listItems"
        class="list__item"
        :key="item[itemFieldId]"
      >
        <label
          @click="_handleItemClick(item, index)"
          :class="[
            'list__item-content',
            { 'list__item-content_no-toggle': !hasToggle },
          ]"
        >
          <Checkbox
            v-if="hasToggle"
            @input="selected => _handleToggleInput(index, selected)"
            :disabled="item.disabled"
            :value="item.selected"
            :ref="item[itemFieldId]"
          />
          <slot name="itemContent" :column="item" :index="index">
            {{ item[itemFieldName] }}
          </slot>
        </label>
      </li>
    </ul>
  </div>
</template>

<script>
import { VueNestable, VueNestableHandle } from 'vue-nestable/dist/index.esm';

import Checkbox from './../Checkbox';

export default {
  name: 'List',
  components: { Checkbox, VueNestable, VueNestableHandle },
  props: {
    items: { default: () => [], type: Array },
    itemsStyle: { default: '', type: [Object, String] },
    itemContentStyle: { default: '', type: [Object, String] },
    itemFieldId: { default: 'id', type: String },
    itemFieldName: { default: 'name', type: String },
    itemFieldNested: { default: 'items', type: String },
    maxDepth: { default: 1, type: Number },
    multiple: { default: false, type: Boolean },
    sortable: { default: false, type: Boolean },
    sortType: { default: 'dnd', type: String },
    toggleType: { default: 'checkbox', type: String },
    value: { default: () => null, type: [Array, Number, String] },
    vueVersion: { default: 3, type: Number },
  },
  emits: {
    input: null,
    inputIdx: null,
  },
  data: () => ({
    isMultiple: false,
    listItems: [],
    listValue: null,
    itemRefs: [],
  }),
  computed: {
    hasToggle() {
      return this.toggleType !== 'none';
    },
    sortOptions() {
      return {
        childrenProp: this.itemFieldNested,
        maxDepth: this.maxDepth,
        value: this.items,
      };
    },
  },
  watch: {
    items: {
      deep: true,
      immediate: true,
      handler(items) {
        this.updateList(items, this.value);
      },
    },
    value: {
      deep: true,
      immediate: true,
      handler(value) {
        this.updateList(this.items, value);
      },
    },
  },
  created() {
    this.updateList(this.items, this.value);
  },
  beforeUpdate() {
    this.itemRefs = [];
  },
  methods: {
    /* UI */
    _handleItemClick(item, index) {
      const itemId = item[this.itemFieldId];

      if (this.hasToggle) {
        let itemRef;
        if (this.vueVersion === 3) {
          // TODO:
        } else {
          itemRef = this.$refs[itemId];
          if (Array.isArray(itemRef)) {
            itemRef = itemRef[0];
          }
        }
        itemRef?.click?.();
      } else {
        this._handleToggleInput(index, this.multiple ? !item.selected : true);
      }
    },
    _handleToggleInput(index, selected) {
      const listItems = this.listItems;
      const itemFieldId = this.itemFieldId;
      const itemFieldIdValue = listItems[index][itemFieldId];

      listItems.splice(index, 1, { ...listItems[index], selected });

      if (this.isMultiple) {
        selected
          ? this.listValue.push(itemFieldIdValue)
          : this.listValue.splice(
              this.listValue.findIndex(v => v === itemFieldIdValue),
              1
            );

        this.$emit('input', this.listValue);
        this.$emit('inputIdx', index);
        return;
      }

      if (!selected) {
        this.listValue = null;

        this.$emit('input', this.listValue);
        this.$emit('inputIdx', index);
        return;
      }

      const prevVal = this.listValue;
      this.listValue = itemFieldIdValue;

      const prevIdx = listItems.findIndex(i => i[itemFieldId] === prevVal);
      if (prevIdx < 0) {
        this.$emit('input', this.listValue);
        this.$emit('inputIdx', index);
        return;
      }

      listItems.splice(prevIdx, 1, { ...listItems[prevIdx], selected: false });
      this.$emit('input', this.listValue);
      this.$emit('inputIdx', index);
    },
    setItemRef(el) {
      if (el) {
        this.itemRefs.push(el);
      }
    },
    /* Methods */
    updateList(items, value) {
      const itemFieldId = this.itemFieldId;

      this.listItems = Array.isArray(value)
        ? items.map(i =>
            value.includes(i[itemFieldId]) ? { ...i, selected: true } : i
          )
        : items.map(i =>
            value === i[itemFieldId] ? { ...i, selected: true } : i
          );
      this.isMultiple = Array.isArray(value) || this.multiple;
      this.listValue = this.isMultiple ? value || [] : value;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './List';
</style>

<style lang="scss">
@import './Nestable';
</style>
